import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, Chip, CircularProgress,
  IconButton, Tooltip, Skeleton, TextField, Collapse, Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lightbulb as IdeaIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformColor, getPlatformName } from '../config/platforms';
import { PostThumbnail } from '../components/PostThumbnail';
import { SchedulePostDialog } from '../components/SchedulePostDialog';
import { EditPostDialog } from '../components/EditPostDialog';
import { DeletePostDialog } from '../components/DeletePostDialog';
import { usePostActions } from '../components/usePostActions';

const API_URL = process.env.REACT_APP_API_URL || '';

/** Matches Post History, so the two pages page at the same rate. */
const PAGE_SIZE = 24;

interface Idea {
  id: string;
  content: string;
  media_urls: string[];
  platforms: string[];
  status: string;
  created_at: string;
  updated_at?: string;
  content_variants?: Record<string, string> | null;
  scheduled_for?: string | null;
  schedule_variants?: Record<string, string> | null;
}

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Somewhere to put a post before you know when it should go out.
 *
 * These are **drafts** — the same `status: 'draft'` the composer has always written, and the
 * same rows Post History shows under its Draft filter. The status existed and worked; what was
 * missing was a place to see them and a way to pull one back out, so nobody had any. Giving one
 * a time turns it into a scheduled post, with no copy and nothing to clean up afterwards.
 *
 * Deliberately not a separate table. Two parallel concepts would mean two places to look for
 * the same thing and posts that cannot move between them.
 */
export const IdeaStaging: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * Quick capture: text, optional media, nothing else.
   *
   * The composer asks which networks and when, which is exactly the decision staging exists
   * to defer. This is the "dump it now, decide later" path — the first version of this page
   * sent people to the composer, which still demanded a platform before it would save.
   */
  const [composing, setComposing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftMedia, setDraftMedia] = useState<{ id: string; url: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [scheduling, setScheduling] = useState<Idea | null>(null);
  const [editing, setEditing] = useState<Idea | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Idea | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      // Paged rather than capped. The previous limit of 100 silently dropped the 101st idea
      // with nothing on screen to say so.
      const res = await fetch(
        `${API_URL}/api/posts?status=draft&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Could not load ideas');
      setIdeas(await res.json());
      setTotal(Number(res.headers.get('X-Total-Count') || 0));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load ideas');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => { load(); }, [load]);

  const actions = usePostActions(load);

  const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';   // so picking the same file twice still fires
    if (!file || !token) return;
    setUploading(true);
    try {
      const res = await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type },
        body: file,
      });
      const data = await res.json();
      if (res.ok) setDraftMedia(prev => [...prev, data]);
      else setError(data.error || 'Upload failed');
    } catch {
      setError('Network error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const saveIdea = async () => {
    if (!token || (!draftText.trim() && draftMedia.length === 0)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/posts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // No platforms and no time — that is the whole point. The server accepts a draft
        // without either; everything else still requires them.
        body: JSON.stringify({
          content: draftText,
          mediaUrls: draftMedia.map(m => m.url),
          platforms: [],
          status: 'draft',
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not save'); return; }
      setDraftText('');
      setDraftMedia([]);
      setComposing(false);
      setError('');
      await load();
    } catch {
      setError('Network error saving the idea');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Idea Staging</Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}><CardContent>
              <Skeleton width="50%" height={28} />
              <Skeleton /><Skeleton width="70%" />
            </CardContent></Card>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Idea Staging</Typography>
          <Typography variant="body2" color="text.secondary">
            Posts written but not yet given a date. Give one a time and it becomes a scheduled
            post.{total > 0 ? ` ${total} staged.` : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setComposing(v => !v)}
        >
          New idea
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Collapse in={composing}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              autoFocus
              placeholder="What's the idea? Paste the copy here — you can pick the networks and the day later."
              value={draftText}
              onChange={e => setDraftText(e.target.value)}
              sx={{ mb: 2 }}
            />

            {draftMedia.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {draftMedia.map(m => (
                  <Box key={m.id} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={m.url}
                      alt=""
                      sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setDraftMedia(prev => prev.filter(x => x.id !== m.id))}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon />}
                disabled={uploading}
              >
                Add media
                <input hidden type="file" accept="image/*,video/*" onChange={uploadFile} />
              </Button>

              <Box sx={{ flexGrow: 1 }} />

              <Button size="small" onClick={() => { setComposing(false); setDraftText(''); setDraftMedia([]); }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={saveIdea}
                disabled={saving || uploading || (!draftText.trim() && draftMedia.length === 0)}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                Save idea
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              No network or time needed — choose those when you schedule it. For per-network
              captions or a preview, use the full composer instead.
            </Typography>
          </CardContent>
        </Card>
      </Collapse>

      {ideas.length === 0 ? (
        <Alert severity="info" icon={<IdeaIcon />}>
          <strong>Nothing staged yet.</strong> Write a post in the composer and choose{' '}
          <strong>Save as Draft</strong> instead of scheduling it, and it will wait here until
          you know when it should go out. The composer can then start from any idea stored here.
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {ideas.map(idea => {
            const thumbnail = idea.media_urls?.[0];
            const busy = actions.busyId === idea.id;
            return (
              <Card key={idea.id} sx={{ display: 'flex', flexDirection: 'column', opacity: busy ? 0.6 : 1 }}>
                {thumbnail && <PostThumbnail src={thumbnail} />}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Saved {formatWhen(idea.updated_at || idea.created_at)}
                  </Typography>

                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {idea.content.slice(0, 180)}{idea.content.length > 180 ? '…' : ''}
                  </Typography>

                  {/* Moved back from the schedule: say its times survived, so scheduling it
                      again is expected to start from them rather than a blank picker. */}
                  {idea.platforms?.some(p => idea.schedule_variants?.[p] || idea.scheduled_for) && (
                    <Typography variant="caption" color="text.secondary">
                      Kept times:{' '}
                      {idea.platforms
                        .map(p => ({ p, at: idea.schedule_variants?.[p] || idea.scheduled_for }))
                        .filter((x): x is { p: string; at: string } => Boolean(x.at))
                        .sort((a, b) => a.at.localeCompare(b.at))
                        .map(({ p, at }) => `${getPlatformName(p)} ${new Date(at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}`)
                        .join(' · ')}
                    </Typography>
                  )}

                  {idea.platforms?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {idea.platforms.map(p => (
                        <Chip key={p} size="small" label={getPlatformName(p)}
                          sx={{ height: 20, bgcolor: getPlatformColor(p), color: '#fff' }} />
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={busy ? <CircularProgress size={14} /> : <ScheduleIcon />}
                      disabled={busy}
                      // An idea with no networks yet cannot go straight to a time — it would
                      // be scheduled to publish nowhere. Send it to the composer, which is
                      // where networks are chosen, already loaded.
                      onClick={() =>
                        idea.platforms?.length
                          ? setScheduling(idea)
                          : navigate(`/create?idea=${idea.id}`)
                      }
                    >
                      {idea.platforms?.length ? 'Schedule' : 'Choose networks'}
                    </Button>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => setEditing(idea)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => setConfirmDelete(idea)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(total / PAGE_SIZE)}
            page={page + 1}
            onChange={(_, value) => {
              setPage(value - 1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
          />
        </Box>
      )}

      {scheduling && (
        <SchedulePostDialog
          open
          platforms={scheduling.platforms}
          postId={scheduling.id}
          initial={scheduling.scheduled_for}
          initialVariants={scheduling.schedule_variants}
          busy={actions.busyId === scheduling.id}
          onClose={() => setScheduling(null)}
          onConfirm={async (iso, variants) => {
            // Scheduling turns this very post into a scheduled one, so it leaves staging by
            // itself — no copy, nothing to tidy up.
            const ok = await actions.schedule(scheduling as never, iso, variants);
            if (ok) setScheduling(null);
          }}
        />
      )}

      {editing && (
        <EditPostDialog
          open
          post={editing as never}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <DeletePostDialog
        post={confirmDelete as never}
        busy={actions.busyId === confirmDelete?.id}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async (post, removeFromPlatforms) => {
          const ok = await actions.remove(post as never, removeFromPlatforms);
          if (ok) setConfirmDelete(null);
          return ok;
        }}
      />
    </Box>
  );
};

export default IdeaStaging;
